import { useState } from 'react';
import { Avatar, Badge, Button, Divider } from 'src/components/atoms';
import { BadgeVariant } from 'src/components/atoms/Badge/Badge.styles';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import { hiddenDots } from 'src/components/pages/Case/helpers/hiddenDots';
import { CaseLinkType, ReportStatus } from 'src/graphql';
import { useTheme } from 'styled-components';
import { FormInputVariant, FormState } from 'src/types';
import { capitaliseFirstLetter, returnDashIfNoValue } from 'src/utils';
import { getInitials } from 'src/utils/getInitials';
import { DateFormats, formatDate } from 'src/utils/time';
import { FormInput } from '../FormInput';
import { Modal } from '../Modal';
import {
  AvatarContainer,
  DetailLabel,
  DetailsGrid,
  DetailValue,
  DividerContainer,
  NotesContainer,
} from './LinkedCaseDetailsModal.styles';

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  canViewCaseDetails: boolean;
  canUnlinkCase: boolean;
  caseId?: string | null;
  caseName?: string | null;
  caseStatus?: ReportStatus | null;
  linkDate: string;
  linkType: CaseLinkType;
  notes?: string | null;
  linkedBy?: string | null;
  readableId?: string | null;
  unlinkCase: () => void;
  onViewLinkedCase: () => void;
}

export const LinkedCaseDetailsModal = ({
  open,
  setOpen,
  canViewCaseDetails,
  canUnlinkCase,
  caseId,
  caseName,
  caseStatus,
  linkDate,
  linkType,
  notes,
  linkedBy,
  readableId,
  unlinkCase,
  onViewLinkedCase,
}: Props) => {
  const theme = useTheme();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const handleOnViewLinkedCaseClicked = () => {
    onViewLinkedCase();
    window.open(`/case?id=${caseId}`, '_blank');
  };

  return (
    <>
      <Modal
        size="medium"
        title="Linked Case Details"
        confirmButtonText="Open Case"
        includeClose
        open={open}
        setOpen={setOpen}
        confirmButtonIcon={{ position: 'left', className: 'uil uil-file-alt' }}
        alternativeSecondaryButton={
          <Button
            disabled={!canUnlinkCase}
            variant="secondary"
            noTextWrap
            data-testid="unlink-cases-button"
            onClick={() => setConfirmDeleteOpen(true)}
          >
            <StyledIconInButton
              className="uil uil-link"
              size="medium"
              position="left"
            />
            Unlink Case
          </Button>
        }
        onCancel={() => {
          setOpen(false);
        }}
        onConfirm={handleOnViewLinkedCaseClicked}
        data-testid="linked-case-details-modal"
        isConfirmButtonDisabled={!canViewCaseDetails}
        closeOnOutsideClick={!confirmDeleteOpen}
      >
        <DetailsGrid marginTop="16px">
          <div data-testid="case-id-detail">
            <DetailLabel>Case ID</DetailLabel>
            {canViewCaseDetails ? (
              <Button
                variant="secondary"
                disabled={!canViewCaseDetails}
                noTextWrap
                onClick={() =>
                  canViewCaseDetails ? handleOnViewLinkedCaseClicked() : null
                }
                data-testid="case-id-detail-button"
              >
                <StyledIconInButton
                  className="uil uil-file-alt"
                  size="medium"
                  position="left"
                />
                {readableId}
              </Button>
            ) : (
              <DetailValue>{hiddenDots}</DetailValue>
            )}
          </div>
          <div data-testid="case-name-detail">
            <DetailLabel>Case Name</DetailLabel>
            <DetailValue>
              {canViewCaseDetails ? returnDashIfNoValue(caseName) : hiddenDots}
            </DetailValue>
          </div>
          <div data-testid="link-date-detail">
            <DetailLabel>Linking Date</DetailLabel>
            <DetailValue>
              {formatDate(linkDate, DateFormats.DD_MM_YYYY)}
            </DetailValue>
          </div>
          <div data-testid="case-status-detail">
            <DetailLabel>Case Status</DetailLabel>
            {canViewCaseDetails && caseStatus ? (
              <Badge
                variant={caseStatus.toLowerCase() as BadgeVariant}
                text={capitaliseFirstLetter(caseStatus)}
              />
            ) : (
              <DetailValue>{hiddenDots}</DetailValue>
            )}
          </div>
        </DetailsGrid>
        <DividerContainer>
          <Divider
            variant="horizontal"
            color={theme.atlas.semantic.color.outline.default}
          />
        </DividerContainer>
        <DetailsGrid>
          <div data-testid="linked-by-detail">
            <DetailLabel>Linked by</DetailLabel>
            <AvatarContainer>
              <Avatar
                variant="text"
                size="small"
                text={getInitials(linkedBy)}
              />
              <DetailValue>{returnDashIfNoValue(linkedBy)}</DetailValue>
            </AvatarContainer>
          </div>
          <div data-testid="link-type-detail">
            <DetailLabel>Link type</DetailLabel>
            <Badge
              variant="draft"
              text={capitaliseFirstLetter(linkType.replaceAll('_', ' '))}
            />
          </div>
          <NotesContainer data-testid="notes-detail">
            <FormInput
              isRequired={false}
              label="Notes"
              data-testid="notes"
              variant={FormInputVariant.AREA}
              state={FormState.READONLY}
              value={returnDashIfNoValue(notes)}
              $removeMarginBottom
            />
          </NotesContainer>
        </DetailsGrid>
      </Modal>
      <Modal
        open={confirmDeleteOpen}
        setOpen={setConfirmDeleteOpen}
        size="small"
        title="Confirm Unlinking Case"
        data-testid="confirm-delete-link-modal"
        description={
          <>
            Are you sure you want to unlink case {readableId} from this case?
            This action will remove the connection between the cases but will
            not delete any data.
          </>
        }
        confirmButtonText="Unlink Case"
        confirmButtonVariant="error"
        cancelButtonText="Cancel"
        onCancel={() => setConfirmDeleteOpen(false)}
        onConfirm={unlinkCase}
        closeOnOutsideClick={false}
      />
    </>
  );
};
